import { ChangeEvent, useEffect, useState } from 'react';
import axios from 'axios';
import { Switch } from '@mui/material';
import { useStore } from 'providers';
import { sendMarketingPreference, sendUserData } from 'services/loyaltyGateway';
import { usePageTitle } from 'hooks';
import { pushAnalyticsEvent } from 'helpers/analytics';
import { Profile, Customer } from 'models';
import Layout from 'layouts';
import Loader from 'components/Loader';
import { AlertState } from 'components/Alert';
import { validateEmail } from 'utils';
import EditProfileForm from './EditProfileForm';
import {
  StyledContainer,
  Header,
  Title,
  SubTitle,
  StyledButtonWithSpinner,
  StyledAlert,
  MarketingPreferences,
  StyledFormControlLabel,
} from './EditProfile.styles';

interface EditFormState {
  firstName: boolean;
  lastName: boolean;
  email: boolean;
  marketingOptIn: boolean;
}

type UserProfileData = 'firstName' | 'lastName' | 'email';

interface ProfileChangeEvent {
  name: UserProfileData;
  value: string;
}

const alerts: Record<string, AlertState> = {
  changeSuccess: { type: 'success', isVisible: true, text: 'Change successful.' },
  changeError: { type: 'error', isVisible: true, text: 'Change failed.' },
};

const initialFormState: EditFormState = {
  firstName: false,
  lastName: false,
  email: false,
  marketingOptIn: false,
};

const EditProfile = () => {
  usePageTitle('Edit Profile');

  const { store, dispatch } = useStore();
  const { userData } = store;
  const { marketingOptIn, canEdit } = userData?.personalDetails || {};

  const oldProfile: Profile = { ...userData?.personalDetails };

  const [newProfile, setNewProfile] = useState<Profile>(oldProfile);
  const [isSaving, setIsSaving] = useState(false);
  const [isInvalidEmail, setIsInvalidEmail] = useState(false);
  const [alert, setAlert] = useState<AlertState | null>(null);
  const [isOptedIn, setIsOptedIn] = useState(false);
  const [formState, setFormState] = useState(initialFormState);

  useEffect(() => {
    setIsOptedIn(!!marketingOptIn);
  }, [marketingOptIn, setIsOptedIn]);

  const handleOnMarketingToggle = (event: ChangeEvent<HTMLInputElement>) => {
    const currentChoice = event.target.checked;
    const updatedProfile: Profile = { ...newProfile, marketingOptIn: currentChoice };

    setIsInvalidEmail(false);
    setNewProfile(updatedProfile);
    setIsOptedIn(currentChoice);
    setFormState((prev) => ({ ...prev, marketingOptIn: currentChoice !== marketingOptIn }));
    setAlert(null);
  };

  const handleOnProfileEdit = (event: ChangeEvent<HTMLInputElement>) => {
    const { name: key, value } = event.target as ProfileChangeEvent;

    const updatedProfile: Profile = { ...oldProfile, ...newProfile, [key]: value.trim() };
    const updatedFormState = updatedProfile[key] !== oldProfile[key];

    setIsInvalidEmail(false);
    setNewProfile(updatedProfile);
    setFormState((prev) => ({ ...prev, [key]: updatedFormState }));
    setAlert(null);
  };

  const handleOnSubmit = async () => {
    if (newProfile?.email && !validateEmail(newProfile.email)) {
      setIsInvalidEmail(true);
      return;
    }

    setIsSaving(true);

    const hasMarketingPreferenceChanges = isOptedIn !== marketingOptIn;

    const updatedPersonalDetails = { ...userData?.personalDetails, ...newProfile };

    try {
      if (hasMarketingPreferenceChanges) sendMarketingPreference(isOptedIn);
      if (canEdit) await sendUserData(updatedPersonalDetails);
    } catch (e) {
      let errorMessage = 'Change failed.';
      if (axios.isAxiosError(e) && e.response?.status === 400 && e.response?.data.message) {
        errorMessage = e.response?.data.message;
      }
      setIsSaving(false);
      setAlert({ ...alerts.changeError, text: errorMessage });
      return;
    }

    const updatedUserData = { ...userData, personalDetails: updatedPersonalDetails } as Customer;
    const resetForm = Object.keys(formState).map((key) => [key, false]);
    const updatedFormState: EditFormState = Object.fromEntries(resetForm);

    dispatch({
      type: 'setUserData',
      payload: { userData: updatedUserData },
    });

    setFormState(updatedFormState);
    setIsSaving(false);
    setAlert(alerts.changeSuccess);
    pushAnalyticsEvent({ event: 'userUpdateDetails' });
    pushAnalyticsEvent({ event: 'marketingPreference', optedIn: isOptedIn, location: 'Settings' });
  };

  let saveButtonText = isSaving ? 'Saving' : 'Save Changes';
  const hasErrored = alert && alert.type === 'error' && !isSaving;
  if (hasErrored) saveButtonText = 'Try again';

  const hasChanges = Object.values(formState).some((hasChanged) => hasChanged);
  const isDisabled = !hasChanges || isInvalidEmail || isSaving;

  return (
    <Layout ariaLabel="Edit Profile">
      {userData ? (
        <StyledContainer>
          <Header>
            <Title variant="h2">Settings</Title>
          </Header>

          <SubTitle variant="h3">Personal Details</SubTitle>

          <EditProfileForm
            profile={userData.personalDetails}
            isInvalidEmail={isInvalidEmail}
            onChange={handleOnProfileEdit}
          />

          <MarketingPreferences>
            <SubTitle variant="h3">Contact Preferences</SubTitle>

            <StyledFormControlLabel
              control={
                <Switch
                  aria-label="Toggle Marketing Preference"
                  color="secondary"
                  checked={isOptedIn}
                  onChange={handleOnMarketingToggle}
                />
              }
              label="Get exclusive Zillionaires' Club offers, limited time deals and special news from Zizzi delivered straight to your inbox"
              labelPlacement="start"
            />
          </MarketingPreferences>

          {alert && <StyledAlert text={alert.text} type={alert.type} />}

          <StyledButtonWithSpinner
            fullWidth
            color="primary"
            variant="contained"
            onClick={handleOnSubmit}
            disabled={isDisabled}
            showSpinner={isSaving}
          >
            {saveButtonText}
          </StyledButtonWithSpinner>
        </StyledContainer>
      ) : (
        <Loader />
      )}
    </Layout>
  );
};

export default EditProfile;
